<script lang="ts">
	import { Button, Checkbox, Radio, Slider, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { setVentoraFan } from 'src/services/ventora_fan';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		patchFan0StatusFromFanSetResponse,
		readVentoraFanSpeedPercent,
		ventoraClamp,
		ventoraHasValidationErrors,
		ventoraNumericValidation,
	} from './ventora_fan_settings_helpers';

	export let device;

	const id = real_id(device.id);

	let loading = false;
	let dirty = false;
	let draftRunMode: 'auto' | 'manual' = 'manual';
	let draftTurn = false;
	let draftPosition = 1;

	$: statusStore = getDeviceStatus(id);
	$: fan0 = $statusStore?.['fan:0'];
	$: savedRunMode = fan0?.mode === 'auto' ? 'auto' : 'manual';

	$: if (!dirty) {
		draftRunMode = savedRunMode as 'auto' | 'manual';
		draftTurn = !!fan0?.on;
		draftPosition = ventoraClamp(
			readVentoraFanSpeedPercent(fan0 as Record<string, unknown>),
			1,
			100,
		);
	}

	$: speedValidation = ventoraNumericValidation(draftPosition, 1, 100);
	$: hasValidationErrors = draftRunMode === 'manual' && ventoraHasValidationErrors([speedValidation]);

	$: sliderA11y = {
		ariaLabel: 'Ventora manual fan speed',
		ariaLabelValue: $format('ventora_manual_mode_speed', {
			values: { speed: draftPosition },
			default: 'Speed',
		}),
	};

	const selectRunMode = (runMode: 'auto' | 'manual') => {
		draftRunMode = runMode;
		dirty = true;
	};

	const save = async () => {
		try {
			if (!dirty || hasValidationErrors) return;
			loading = true;
			let ok = false;
			if (draftRunMode === 'auto') {
				const fanRes = await setVentoraFan(device, { id: 0, mode: 'auto' });
				const fanResp = onSettingsResponse(fanRes, device);
				ok = !!fanResp?.isok;
				if (ok)
					patchFan0StatusFromFanSetResponse(
						device,
						fanResp as unknown as Record<string, unknown>,
					);
			} else {
				const fanRes = await setVentoraFan(device, {
					id: 0,
					mode: 'manual',
					turn: draftTurn,
					speed: ventoraClamp(draftPosition, 1, 100),
				});
				const fanResp = onSettingsResponse(fanRes, device);
				ok = !!fanResp?.isok;
				if (ok)
					patchFan0StatusFromFanSetResponse(
						device,
						fanResp as unknown as Record<string, unknown>,
					);
			}
			if (ok) dirty = false;
		} catch (error) {
			console.error(error);
		} finally {
			loading = false;
		}
	};
</script>

<Text styleType="body-small" class="mb-s">
	{$format('sub_settings_title_ventora_run_mode_hint', {
		default: 'Runtime control (Fan.Set). This is separate from input mode above.',
	})}
</Text>
<Radio
	size="m"
	selected={draftRunMode}
	value="auto"
	on:click={() => selectRunMode('auto')}
	id="radio-ventora-run-mode-auto"
>
	{$format('ventora_run_mode_auto', { default: 'Auto' })}
</Radio>
<Radio
	size="m"
	selected={draftRunMode}
	value="manual"
	on:click={() => selectRunMode('manual')}
	id="radio-ventora-run-mode-manual"
>
	{$format('ventora_run_mode_manual', { default: 'Manual' })}
</Radio>

{#if draftRunMode === 'manual'}
	<Checkbox
		size="m"
		class="mb-m"
		bind:checked={draftTurn}
		on:click={() => {
			dirty = true;
		}}
		id="checkbox-ventora-manual-on-draft"
	>
		{$format('ventora_manual_mode_turn_fan_on', { default: 'Turn the fan ON' })}
	</Checkbox>
	<Slider
		id="slider-ventora-manual-speed-draft"
		a11yProps={sliderA11y}
		class="mb-s"
		type="simple"
		value={draftPosition}
		min={1}
		max={100}
		step={1}
		unit="%"
		stopValueLabel
		title={$format('ventora_manual_mode_speed', {
			values: { speed: draftPosition },
			default: 'Speed',
		})}
		on:value_change={(e) => {
			draftPosition = e.detail.value;
			dirty = true;
		}}
	/>
{/if}

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-run-mode-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
